import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Copy, Trash2, Lock, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { api, errMsg, formatDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { downloadPdf } from "../lib/download";

const ResumeCard = ({ r, unlocked, onChange }) => {
  const navigate = useNavigate();
  const run = (fn, ok) => async () => {
    try { await fn(); onChange(); toast.success(ok); } catch (e) { toast.error(errMsg(e)); }
  };
  const dup = run(() => api.post(`/resumes/${r.resume_id}/duplicate`), "Duplicated");
  const doDel = run(() => api.delete(`/resumes/${r.resume_id}`), "Deleted");
  const del = () => window.confirm(`Delete "${r.title}"? This cannot be undone.`) && doDel();
  const dl = () => (unlocked ? downloadPdf(r.resume_id).catch((e) => toast.error(errMsg(e))) : navigate("/pricing"));
  return (
    <div data-testid={`resume-card-${r.resume_id}`} className="group rounded-2xl border border-white/10 bg-[#12151E] p-6 flex flex-col hover:border-emerald-500/40 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-semibold truncate" data-testid="resume-card-title">{r.title}</h3>
          <p className="text-xs text-slate-500 mt-1">{r.data.personal.full_name || "No name yet"} · edited {formatDate(r.updated_at)}</p>
        </div>
        <span className={`text-[10px] font-mono uppercase tracking-widest rounded-full px-2.5 py-1 ${unlocked ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"}`}>
          {unlocked ? "Unlocked" : "Locked"}
        </span>
      </div>
      <p className="mt-4 text-xs font-mono text-slate-500 uppercase tracking-widest">Template · {r.template}</p>
      <div className="mt-6 flex items-center gap-2">
        <Button size="sm" onClick={() => navigate(`/builder/${r.resume_id}`)} data-testid="resume-edit-btn" className="rounded-full bg-white/10 text-white hover:bg-white/20 hover:text-white"><Pencil className="h-3.5 w-3.5 mr-1.5" />Edit</Button>
        <Button size="sm" onClick={dl} data-testid="resume-download-btn" className="rounded-full bg-emerald-500 text-black hover:bg-emerald-400 hover:text-black">
          {unlocked ? <Download className="h-3.5 w-3.5 mr-1.5" /> : <Lock className="h-3.5 w-3.5 mr-1.5" />}PDF
        </Button>
        <button onClick={dup} data-testid="resume-duplicate-btn" className="ml-auto p-2 text-slate-500 hover:text-white transition-colors" aria-label="Duplicate"><Copy className="h-4 w-4" /></button>
        <button onClick={del} data-testid="resume-delete-btn" className="p-2 text-slate-500 hover:text-red-400 transition-colors" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [resumes, setResumes] = useState(null);
  const [params] = useSearchParams();
  const started = useRef(false);
  const load = () => api.get("/resumes").then((r) => setResumes(r.data));
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const t = params.get("template");
    if (t) {
      api.post("/resumes", { title: "My resume", template: t })
        .then(({ data }) => navigate(`/builder/${data.resume_id}`, { replace: true }))
        .catch(() => load());
    } else load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async () => {
    try {
      const { data } = await api.post("/resumes", { title: "My resume", template: "modern" });
      navigate(`/builder/${data.resume_id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <main className="max-w-7xl mx-auto px-5 sm:px-8 py-14 relative z-10" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-emerald-400">Workspace</p>
          <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight">Hi, {user.name.split(" ")[0]}</h1>
        </div>
        <Button onClick={create} data-testid="create-resume-btn" className="rounded-full h-11 px-6 bg-emerald-500 text-black font-semibold hover:bg-emerald-400 hover:text-black">
          <Plus className="h-4 w-4 mr-1.5" /> New resume
        </Button>
      </div>
      {resumes && resumes.length === 0 && (
        <div className="mt-14 rounded-2xl border border-dashed border-white/15 p-14 text-center" data-testid="dashboard-empty">
          <p className="text-slate-400">No resumes yet. Start one — it only takes a few minutes.</p>
        </div>
      )}
      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {resumes?.map((r) => <ResumeCard key={r.resume_id} r={r} unlocked={user.has_access} onChange={load} />)}
      </div>
    </main>
  );
}
